import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AssetUrls } from 'config/assets';
import { PublicSetCardAll } from 'store/services/sets/publicSetsApi';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './NotableCardsGrid.css';

interface NotableCardsGridProps {
  cards: PublicSetCardAll[];
  setCode: string;
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatUsd = (n: number | null): string => {
  if (n == null || n <= 0) return '';
  return USD_FORMATTER.format(n);
};

const NotableCardItem: React.FC<{ card: PublicSetCardAll; setCode: string }> = ({ card, setCode }) => {
  const initialSrc = card.imageUris?.normal
    ? AssetUrls.cards.ensureCDNUrl(card.imageUris.normal)
    : AssetUrls.cards.getCardImage(card.scryfallID, 'normal');

  const [imageSrc, setImageSrc] = useState(initialSrc);
  const [fallbackAttempt, setFallbackAttempt] = useState(0);

  const handleImageError = useCallback(() => {
    const fallbacks = [
      AssetUrls.cards.getCardImage(card.scryfallID, 'small'),
      AssetUrls.cards.getCardImage(card.scryfallID, 'large'),
      AssetUrls.cards.defaultBack,
    ];
    if (fallbackAttempt < fallbacks.length) {
      setImageSrc(fallbacks[fallbackAttempt]);
      setFallbackAttempt((prev) => prev + 1);
    }
  }, [card.scryfallID, fallbackAttempt]);

  const href = buildCardUrl({
    name: card.name,
    setCode,
    collectorNumber: card.collectorNumber,
    scryfallID: card.scryfallID,
  });

  const priceLabel = formatUsd(card.usd);

  return (
    <Link to={href} className="notable-card" aria-label={`View ${card.name}`}>
      <div className="notable-card-image-wrapper">
        <img
          src={imageSrc}
          alt={card.name}
          className="notable-card-image"
          loading="lazy"
          decoding="async"
          onError={handleImageError}
        />
      </div>
      <div className="notable-card-caption">
        <span className="notable-card-name" title={card.name}>{card.name}</span>
        {priceLabel && <span className="notable-card-price">{priceLabel}</span>}
      </div>
    </Link>
  );
};

export const NotableCardsGrid: React.FC<NotableCardsGridProps> = ({ cards, setCode }) => {
  const notable = useMemo(() => {
    return cards
      .filter((c) => !(c.typeLine || '').includes('Basic Land'))
      .filter((c) => c.usd != null && c.usd > 0)
      .sort((a, b) => (b.usd ?? 0) - (a.usd ?? 0))
      .slice(0, 12);
  }, [cards]);

  if (notable.length === 0) return null;

  return (
    <section className="notable-cards" aria-label="Notable cards">
      <h2 className="notable-cards-title">Notable Cards</h2>
      <div className="notable-cards-grid">
        {notable.map((card) => (
          <NotableCardItem
            key={`${card.scryfallID}-${card.collectorNumber}`}
            card={card}
            setCode={setCode}
          />
        ))}
      </div>
    </section>
  );
};
